// app.js
import React from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "@fortawesome/fontawesome-free/js/all.min.js";
import "./App.scss";

import { Navigate, useLocation, createBrowserRouter, RouterProvider, Outlet } from "react-router-dom";
import { signOut, fetchAuthSession } from 'aws-amplify/auth'; // Correct modular import

// Common components
import TopBar from "./components/TopBar";
// Login Page
import LoginPage from "./pages/login/login";
// IoT imports
import IotHome from "./pages/iot/IotHome";
// Product imports
import ProductHome from "./pages/product/ProductHome";
import AddProduct from "./pages/product/AddProduct";
// Log imports
import LogsHome from "./pages/logs/LogsHome";
import AddLog from "./pages/logs/AddLog";
import ViewLogChart from "./pages/logs/ViewLogChart";
import ViewLogData from "./pages/logs/ViewLogData";
// Debug imports
import DebugHome from "./pages/debug/DebugHome";
// Dashboard imports
import DashboardHome from "./pages/dashboard/DashboardHome";
// RC+ imports
import RCPlus from "./pages/rcplus/RCPlus";
// Admin imports
import Admin from "./pages/admin/Admin";
import UnallocatedNetlinks from "./pages/admin/UnallocatedNetlinks";

import ListUploads from "./pages/listuploads/ListUploads";

function App() {

  // RequireAuth Wrapper
  const RequireAuth = ({ children, adminOnly = false }) => {
    const [isAuthenticated, setIsAuthenticated] = React.useState(null);
    const [isAdmin, setIsAdmin] = React.useState(false);
    
    
    React.useEffect(() => {
      let isMounted = true;
  
      const checkUser = async () => {
        try {
          // Check for cached user data first
          const cachedUser = sessionStorage.getItem("currentUser");
          if (cachedUser) {
            const user = JSON.parse(cachedUser);
            setIsAuthenticated(true);
            setIsAdmin(user.isAdmin);
            return;
          }
  
          // Otherwise, fetch a fresh session from Cognito
          const session = await fetchAuthSession();
          if (!isMounted) return;
  
          // Read group membership from the ID token
          const groups = session.tokens.idToken?.payload["cognito:groups"] || [];
          const userData = {
            isAdmin: groups.includes("administrator"),
          };
  
          // Store the user in session storage to avoid repeated API calls
          sessionStorage.setItem("currentUser", JSON.stringify(userData));
  
          setIsAuthenticated(true);
          setIsAdmin(userData.isAdmin);
        } catch (error) {
          if (!isMounted) return;
          setIsAuthenticated(false);
          setIsAdmin(false);
          console.error("Error fetching user:", error);
        }
      };
  
      checkUser();
  
      return () => { isMounted = false; };
    }, []); // Runs only once
  
    if (isAuthenticated === null) {
      return <div>Loading...</div>;
    }
  
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    if (adminOnly && !isAdmin) {
      return <Navigate to="/" replace />; // Redirect non-admins
    }
  
    return children;
  };

  const Layout = () => {
    const location = useLocation();
    const isDashboardRoute = location.pathname.startsWith("/dashboard") || location.pathname.startsWith("/rcplus");
    const pageContainerClass = isDashboardRoute ? "pageContainer pageContainer--dashboard" : "pageContainer";
    const handleSignOut = async () => {
      await signOut(); // Sign out the user
      window.location.href = '/login'; // Force navigation to the login page
    };
  
    return (
      <div className="appContainer">
        <TopBar onSignOut={handleSignOut} /> {/* Pass onSignOut instead of signOut */}
        <div className={pageContainerClass}>
          <Outlet />
        </div>
      </div>
    );
  };

  const router = createBrowserRouter([
    {
      path: "/",
      element: <Navigate to="/login" replace />,
    },
    {
      path: "/login",
      element: <LoginPage />,
    },
    {
      path: "/",
      element: <Layout />,
      children: [
        {
          path: "/",
          element: (
            <RequireAuth>
              <LoginPage />
            </RequireAuth>
          ),
        },
        // Admin controllers
        {
          path: "/admin",
          element: (
            <RequireAuth adminOnly={true}>
              <Admin />
            </RequireAuth>
          ),
        },
        {
          path: "/admin/unallocated",
          element: (
            <RequireAuth adminOnly={true}>
              <UnallocatedNetlinks />
            </RequireAuth>
          ),
        },
        // IoT devices
        {
          path: "/iot",
          element: (
            <RequireAuth>
              <IotHome />
            </RequireAuth>
          ),
        },

        // Product controllers
        {
          path: "/product",
          element: (
            <RequireAuth>
              <ProductHome />
            </RequireAuth>
          ),
        },
        {
          path: "/product/add",
          element: (
            <RequireAuth>
              <AddProduct />
            </RequireAuth>
          ),
        },

        // Log controllers
        {
          path: "/logs/:id",
          element: (
            <RequireAuth>
              <LogsHome />
            </RequireAuth>
          ),
        },
        {
          path: "/dashboard/:id",
          element: (
            <RequireAuth>
              <DashboardHome />
            </RequireAuth>
          ),
        },
        {
          path: "/rcplus/:id",
          element: (
            <RequireAuth>
              <RCPlus />
            </RequireAuth>
          ),
        },
        {
          path: "/debug/:id",
          element: (
            <RequireAuth>
              <DebugHome />
            </RequireAuth>
          ),
        },
        {
          path: "/logs/add/:id",
          element: (
            <RequireAuth>
              <AddLog />
            </RequireAuth>
          ),
        },
        {
          path: "/logs/chart/:id",
          element: (
            <RequireAuth>
              <ViewLogChart />
            </RequireAuth>
          ),
        },
        {
          path: "/logs/data/:id",
          element: (
            <RequireAuth>
              <ViewLogData />
            </RequireAuth>
          ),
        },
        {
          path: "/list-uploads/:product/:serialNumber",
          element: (
            <RequireAuth>
              <ListUploads />
            </RequireAuth>
          ),
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
}

export default App;
